import './index.css';
import App from './App';
import React from 'react';
import { Provider } from 'react-redux';
import ReactDOM from 'react-dom/client';
import { store, persistor } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initMixpanel } from './lib/mixpanel';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is "fresh" for 5 minutes: pages opened within that window use the
      // cache with no request. After that, cached data still shows instantly
      // but is refreshed in the background, so prices and stock stay current.
      staleTime: 1000 * 60 * 5,
      // How long data no screen is using stays in memory before it's dropped
      gcTime: 1000 * 60 * 30,
      retry: 1,
      // Refetch on mount only when the cached data is stale (older than
      // staleTime). `false` would never refetch, showing old prices/stock.
      refetchOnMount: true,
    },
  },
});

const token = import.meta.env.VITE_MIXPANEL_TOKEN;
const enableAnalytics = import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

// Initialize only when token present AND analytics enabled (or after user consents)
if (token && enableAnalytics) {
  initMixpanel(token);
}

// Cache the app shell and static files so repeat visits on slow
// connections don't re-download (or re-validate) everything. See public/sw.js.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

const root = document.getElementById('app');

if (!root) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate
        loading={<div className="loader"></div>}
        persistor={persistor}
      >
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
